import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import api from '../../api/client';

function PopoverPortal({ targetRef, children, onClose }) {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!targetRef.current) return;
    const rect = targetRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 8, left: rect.left + rect.width / 2 - 88 });
  }, [targetRef]);
  useEffect(() => {
    const handleClick = (e) => {
      if (targetRef.current && !targetRef.current.contains(e.target)) onClose();
    };
    const timer = setTimeout(() => document.addEventListener('click', handleClick), 10);
    return () => { clearTimeout(timer); document.removeEventListener('click', handleClick); };
  }, [onClose, targetRef]);
  if (!pos) return null;
  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', top: pos.top, left: Math.max(8, Math.min(pos.left, window.innerWidth - 184)), zIndex: 9999 }}>
      {children}
    </div>,
    document.body
  );
}
import { adminApi } from '../../api/admin';
import { recordsApi } from '../../api/records';
import { ChevronDown, Clock, ArrowRight, Eye, Pencil, X, RotateCcw } from 'lucide-react';

export default function StageProgressPath({
  record,
  transitions,
  workflowTransitions,
  onTransition,
  transitioning,
  onViewStage,
  viewingStageId,
  viewingMode,
}) {
  const [workflows, setWorkflows] = useState([]);
  const [history, setHistory] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hoveredStage, setHoveredStage] = useState(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  const [popoverStage, setPopoverStage] = useState(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!record?.record_type_id) return;
    adminApi.getWorkflows(record.record_type_id).then(res => {
      setWorkflows(res.data.workflows || []);
    }).catch(() => {});
  }, [record?.record_type_id]);

  useEffect(() => {
    if (!record?.id) return;
    recordsApi.getHistory(record.id).then(res => {
      setHistory(res.data.history || []);
    }).catch(() => {});
  }, [record?.id, record?.workflow_stage_id]);

  useEffect(() => {
    if (!popoverStage) return;
    const handleClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setPopoverStage(null);
      }
    };
    const timer = setTimeout(() => document.addEventListener('click', handleClick), 10);
    return () => { clearTimeout(timer); document.removeEventListener('click', handleClick); };
  }, [popoverStage]);

  const currentWorkflow = workflows.find(w => w.id === record?.workflow_id);
  const currentStages = currentWorkflow?.stages || [];

  const stageHistory = useMemo(() => {
    const visited = new Map();
    const events = history
      .filter(h => h.action === 'stage_transition')
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    events.forEach(h => {
      const d = h.details || {};
      if (d.from_stage && d.from_stage !== '(none)') {
        if (!visited.has(d.from_stage)) visited.set(d.from_stage, { enteredAt: null, leftAt: h.created_at });
        else visited.get(d.from_stage).leftAt = h.created_at;
      }
      if (d.to_stage) {
        if (!visited.has(d.to_stage)) visited.set(d.to_stage, { enteredAt: h.created_at, leftAt: null });
        else visited.get(d.to_stage).enteredAt = h.created_at;
      }
    });
    return visited;
  }, [history]);

  const getStageStatus = (stage) => {
    if (stage.id === record?.workflow_stage_id) return 'current';
    const currentIdx = currentStages.findIndex(s => s.id === record?.workflow_stage_id);
    const stageIdx = currentStages.findIndex(s => s.id === stage.id);
    // Stages after current are future or eligible, even if previously visited
    if (stageIdx > currentIdx) {
      const h = stageHistory.get(stage.name);
      const wasVisited = h && (h.leftAt || h.enteredAt);
      if (eligibleMap[stage.id]) return wasVisited ? 'eligible_visited' : 'eligible';
      return wasVisited ? 'future_visited' : 'future';
    }
    // Stages before current
    const h = stageHistory.get(stage.name);
    if (h && h.leftAt) return 'completed';
    if (h && h.enteredAt) return 'visited';
    if (eligibleMap[stage.id]) return 'eligible';
    return 'future';
  };

  const currentStageEnteredAt = useMemo(() => {
    const name = currentStages.find(s => s.id === record?.workflow_stage_id)?.name;
    if (!name) return record?.updated_at;
    const e = stageHistory.get(name);
    return e?.enteredAt || record?.created_at;
  }, [stageHistory, record, currentStages]);

  const progressPct = useMemo(() => {
    if (currentStages.length === 0) return 0;
    const idx = currentStages.findIndex(s => s.id === record?.workflow_stage_id);
    return idx === -1 ? 0 : Math.round(((idx + 1) / currentStages.length) * 100);
  }, [currentStages, record?.workflow_stage_id]);

  const timeInStage = useMemo(() => {
    if (!currentStageEnteredAt) return null;
    const mins = Math.floor((new Date() - new Date(currentStageEnteredAt)) / 60000);
    if (mins < 60) return mins + 'm';
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return hrs + 'h';
    return Math.floor(hrs / 24) + 'd';
  }, [currentStageEnteredAt]);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

  const primaryTransition = transitions.find(t => t.style === 'primary' || t.style === 'success');

  // Build set of stage IDs that are eligible transition targets
  const eligibleMap = useMemo(() => {
    const map = {};
    transitions.forEach(t => {
      if (t.to_stage_id) map[t.to_stage_id] = t;
    });
    return map;
  }, [transitions]);
  const allActions = [...transitions, ...(workflowTransitions || [])];

  const handleChevronClick = (stage) => {
    const status = getStageStatus(stage);
    console.log('CHEVRON CLICK:', stage.name, 'status:', status, 'popoverStage:', popoverStage);
    if (status === 'current') {
      if (viewingStageId) onViewStage && onViewStage(null, null);
      setPopoverStage(null);
    } else if (status === 'completed' || status === 'visited') {
      setPopoverStage(popoverStage === stage.id ? null : stage.id);
    } else if (status === 'eligible' || status === 'eligible_visited') {
      const transition = eligibleMap[stage.id];
      if (!transition) return;
      const currentIdx = currentStages.findIndex(s => s.id === record?.workflow_stage_id);
      const targetIdx = currentStages.findIndex(s => s.id === stage.id);
      const skipped = targetIdx > currentIdx + 1 ? targetIdx - currentIdx - 1 : 0;
      const msg = skipped > 0
        ? 'Move to "' + stage.name + '"? This will skip ' + skipped + ' stage(s) in between.'
        : 'Move to "' + stage.name + '"?';
      if (window.confirm(msg)) {
        onTransition(transition);
      }
    } else if (status === 'future_visited') {
      setPopoverStage(popoverStage === stage.id ? null : stage.id);
    }
  };

  const handleViewEdit = (stageId, mode) => {
    setPopoverStage(null);
    onViewStage && onViewStage(stageId, mode);
  };

  const handleRevertToStage = async (stage) => {
    if (!window.confirm('Revert case back to "' + stage.name + '"? The case will return to this stage.')) return;
    setPopoverStage(null);
    try {
      await api.post('/records/' + record.id + '/revert-stage', { stage_id: stage.id });
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to revert stage');
    }
  };

  if (!currentWorkflow || currentStages.length === 0) return null;

  var arrow = 14;

  return (
    <div className="bg-white border border-gray-200 rounded-xl mb-6">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
            {currentWorkflow.name}
          </span>
          <span className="text-[10px] text-gray-400 font-medium bg-gray-200 px-1.5 py-0.5 rounded">
            {progressPct}%
          </span>
          {viewingStageId && (
            <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
              {viewingMode === 'view' ? <Eye className="h-3 w-3" /> : <Pencil className="h-3 w-3" />}
              {viewingMode === 'view' ? 'Viewing' : 'Editing'}: {currentStages.find(s => s.id === viewingStageId)?.name}
              <button onClick={() => onViewStage && onViewStage(null, null)}
                className="ml-0.5 hover:text-amber-900"><X className="h-3 w-3" /></button>
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Clock className="h-3 w-3" />
          <span>{timeInStage} in stage</span>
        </div>
      </div>

      {/* Chevrons + Action */}
      <div className="flex items-stretch">
        <div className="flex-1 py-3 px-3" style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 2, minHeight: 44, overflowX: 'auto', overflowY: 'visible', paddingBottom: 0 }}
                 className="chevron-scroll">
            {currentStages.map(function(stage, idx) {
              var status = getStageStatus(stage);
              var isFirst = idx === 0;
              var isLast = idx === currentStages.length - 1;
              var isClickable = status === 'completed' || status === 'visited' || status === 'current' || status === 'eligible' || status === 'eligible_visited' || status === 'future_visited';
              var isViewing = viewingStageId === stage.id;
              var histEntry = stageHistory.get(stage.name);

              var bgColor, textColor;
              if (status === 'completed') { bgColor = '#16a34a'; textColor = '#fff'; }
              else if (status === 'current') { bgColor = '#f59e0b'; textColor = '#fff'; }
              else if (status === 'visited') { bgColor = '#93c5fd'; textColor = '#1e3a5f'; }
              else if (status === 'eligible') { bgColor = '#64748b'; textColor = '#fff'; }
              else if (status === 'future_visited') { bgColor = '#fef3c7'; textColor = '#92400e'; }
              else if (status === 'eligible_visited') { bgColor = '#fde68a'; textColor = '#78350f'; }
              else { bgColor = '#e5e7eb'; textColor = '#6b7280'; }

              var cp;
              if (isFirst) cp = 'polygon(0 0, calc(100% - ' + arrow + 'px) 0, 100% 50%, calc(100% - ' + arrow + 'px) 100%, 0 100%)';
              else if (isLast) cp = 'polygon(0 0, 100% 0, 100% 100%, 0 100%, ' + arrow + 'px 50%)';
              else cp = 'polygon(0 0, calc(100% - ' + arrow + 'px) 0, 100% 50%, calc(100% - ' + arrow + 'px) 100%, 0 100%, ' + arrow + 'px 50%)';

              return (
                <div key={stage.id} style={{ position: 'relative', flex: '0 0 auto', minWidth: currentStages.length > 12 ? 110 : 0, width: currentStages.length > 12 ? 110 : undefined, flex: currentStages.length > 12 ? '0 0 auto' : '1 1 0%' }}>
                  <button
                    ref={popoverStage === stage.id ? popoverRef : null}
                    type="button"
                    onClick={function() { if (isClickable) handleChevronClick(stage); }}
                    onMouseEnter={function(e) { var r = e.currentTarget.getBoundingClientRect(); setTooltipPos({ top: r.top - 8, left: r.left + r.width / 2 }); setHoveredStage(stage.id); }}
                    onMouseLeave={function() { setHoveredStage(null); }}
                    style={{
                      clipPath: cp,
                      WebkitClipPath: cp,
                      backgroundColor: bgColor,
                      height: 44,
                      width: '100%',
                      border: 'none',
                      padding: 0,
                      margin: 0,
                      outline: isViewing ? '3px solid #f59e0b' : 'none',
                      outlineOffset: -3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      paddingLeft: isFirst ? 12 : arrow + 8,
                      paddingRight: isLast ? 12 : arrow + 8,
                      cursor: isClickable ? 'pointer' : 'default',
                      transition: 'filter 0.15s',
                      filter: hoveredStage === stage.id && isClickable ? 'brightness(1.1)' : 'none',
                    }}
                  >
                    {status === 'completed' && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={textColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, pointerEvents: 'none' }}>
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                    {status === 'current' && (
                      <span className="animate-pulse" style={{
                        width: 8, height: 8, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.8)', flexShrink: 0, pointerEvents: 'none',
                      }} />
                    )}
                    <span style={{
                      fontSize: 11, fontWeight: 600, color: textColor,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1,
                      pointerEvents: 'none',
                    }}>
                      {stage.name}
                    </span>
                  </button>

                  {popoverStage === stage.id && (
                    <PopoverPortal targetRef={popoverRef} onClose={() => setPopoverStage(null)}>
                      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', boxShadow: '0 10px 25px rgba(0,0,0,0.15)', overflow: 'hidden', width: 176 }}>
                        <div style={{ padding: '8px 12px', background: '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
                          <p style={{ fontSize: 11, fontWeight: 600, color: '#374151', margin: 0 }}>{stage.name}</p>
                          {histEntry && (
                            <p style={{ fontSize: 10, color: '#9ca3af', margin: '2px 0 0' }}>
                              {status === 'completed' ? 'Completed ' + fmtDate(histEntry.leftAt) : 'Visited'}
                            </p>
                          )}
                        </div>
                        <div style={{ padding: 6 }}>
                          <button onClick={function() { handleViewEdit(stage.id, 'view'); }}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', fontSize: 12, fontWeight: 500, color: '#374151', background: 'none', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
                            <Eye size={14} color="#9ca3af" /> View Stage
                          </button>
                          <button onClick={function() { handleViewEdit(stage.id, 'edit'); }}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', fontSize: 12, fontWeight: 500, color: '#b45309', background: 'none', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
                            <Pencil size={14} color="#f59e0b" /> Edit Stage
                          </button>
                          <div style={{ borderTop: '1px solid #f3f4f6', margin: '2px 0' }} />
                          <button onClick={function() { handleRevertToStage(stage); }}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', fontSize: 12, fontWeight: 500, color: '#dc2626', background: 'none', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
                            <RotateCcw size={14} color="#dc2626" /> Revert to Stage
                          </button>
                        </div>
                      </div>
                    </PopoverPortal>
                  )}

                  {hoveredStage === stage.id && !popoverStage && (
                    <div style={{ position: 'fixed', top: tooltipPos ? tooltipPos.top : 0, left: tooltipPos ? tooltipPos.left : 0, transform: 'translate(-50%, -100%)', zIndex: 9999, pointerEvents: 'none' }}>
                      <div style={{ background: '#111827', color: '#fff', fontSize: 11, borderRadius: 8, padding: '6px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', whiteSpace: 'nowrap' }}>
                        <p style={{ fontWeight: 600, margin: 0 }}>{stage.name}</p>
                        {status === 'completed' && histEntry && (
                          <p style={{ color: '#9ca3af', marginTop: 2, marginBottom: 0 }}>Completed {fmtDate(histEntry.leftAt)}</p>
                        )}
                        {status === 'current' && (
                          <p style={{ color: '#9ca3af', marginTop: 2, marginBottom: 0 }}>Current · {timeInStage} in stage</p>
                        )}
                        {status === 'eligible' && (
                          <p style={{ color: '#9ca3af', marginTop: 2, marginBottom: 0 }}>Available transition</p>
                        )}
                        {status === 'future' && (
                          <p style={{ color: '#9ca3af', marginTop: 2, marginBottom: 0 }}>Not started</p>
                        )}
                        {isClickable && status !== 'future' && (
                          <p style={{ color: '#93c5fd', marginTop: 2, marginBottom: 0 }}>Click to {status === 'current' ? 'return here' : status === 'eligible' ? 'move here' : 'view/edit'}</p>
                        )}
                      </div>
                      <div style={{ width: 8, height: 8, background: '#111827', transform: 'rotate(45deg)', margin: '-4px auto 0' }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {(workflowTransitions || []).length > 0 && !viewingStageId && (
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', borderLeft: '1px solid #e5e7eb', padding: '0 12px', position: 'relative', zIndex: 60 }}>
            <button onClick={function(e) { e.stopPropagation(); setShowDropdown(!showDropdown); }}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white whitespace-nowrap">
              <ArrowRight className="h-3.5 w-3.5" /> Switch Workflow <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {showDropdown && (
              <React.Fragment>
                <div style={{ position: 'fixed', inset: 0, zIndex: 69 }} onClick={function() { setShowDropdown(false); }} />
                <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, width: 224, background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', boxShadow: '0 10px 25px rgba(0,0,0,0.15)', overflow: 'hidden', zIndex: 70 }}>
                  {(workflowTransitions || []).map(function(t) {
                    return (
                      <button key={'wf-' + t.id}
                        onClick={function() { setShowDropdown(false); onTransition(t); }}
                        disabled={transitioning}
                        style={{ width: '100%', textAlign: 'left', padding: '10px 16px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #f3f4f6', color: '#4338ca', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer' }}>
                        <ArrowRight size={12} />
                        {t.label} {t.to_workflow_name && <span style={{ color: '#9ca3af', marginLeft: 'auto' }}>→ {t.to_workflow_name}</span>}
                      </button>
                    );
                  })}
                </div>
              </React.Fragment>
            )}
          </div>
        )}

        {viewingStageId && (
          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', borderLeft: '1px solid #e5e7eb', padding: '0 12px' }}>
            <button onClick={function() { onViewStage && onViewStage(null, null); }}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-amber-500 hover:bg-amber-600 text-white whitespace-nowrap">
              <ArrowRight className="h-3.5 w-3.5" /> Back to Current
            </button>
          </div>
        )}
      </div>

      <div style={{ height: 4, background: '#f3f4f6' }}>
        <div style={{ height: '100%', background: '#22c55e', transition: 'width 0.5s ease-out', width: progressPct + '%' }} />
      </div>
    </div>
  );
}
